import { useState, useMemo, useEffect, useRef } from "react";
import { useClientId } from "@/shared/hooks/useClientId";
import { useProjects } from "@/features/projects/hooks/useProjects";
import { useDocumentTitle, buildPageTitle } from "@/shared/hooks/useDocumentTitle";
import { useClientAssets } from "@/features/assets/hooks/useClientAssets";
import { useClientAssetsSummary } from "@/features/assets/hooks/useClientAssetsSummary";
import { Card, CardContent } from "@/shared/ui/card";
import { Package, AlertCircle } from "lucide-react";
import { ClientAssetsTable } from "@/features/assets/components/ClientAssetsTable";
import { exportAssets, type AssetExportFormat } from "@/features/exports/services";
import { toast } from "@/shared/hooks/use-toast";
import { useExportsEnabled } from "@/shared/hooks/useExportsEnabled";
import { ClientAssetsHeader } from "./clientAssets/ClientAssetsHeader";
import { ClientAssetsFilters } from "./clientAssets/ClientAssetsFilters";
import { ClientAssetsPagination } from "./clientAssets/ClientAssetsPagination";
import { ClientAssetsLoadingSkeleton } from "./clientAssets/ClientAssetsLoadingSkeleton";
import { AssetDetailPanelLoader } from "./clientAssets/AssetDetailPanelLoader";
import { useFilterOptions } from "./clientAssets/useFilterOptions";

export default function ClientAssets() {
  const { clientId } = useClientId();
  // Pagination state
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;

  // Asset detail drawer state
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);

  // Filter state
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [propertyFilter, setPropertyFilter] = useState<string>("all");

  // MVP2-282: Debounce search input - increased to 500ms for better UX
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // MVP2-282: Track previous search to prevent unnecessary page resets
  const prevSearchRef = useRef(debouncedSearch);

  // Data hooks
  const { projects, loading: projectsLoading } = useProjects();
  // MVP2-282: Don't filter summary by search - keep stats stable while table searches
  const { summary, loading: summaryLoading } = useClientAssetsSummary(
    clientId!,
    statusFilter !== "all" ? statusFilter : undefined,
    typeFilter !== "all" ? typeFilter : undefined,
    propertyFilter !== "all" ? propertyFilter : undefined,
    undefined, // Don't pass search term to summary
  );
  const {
    assets,
    totalCount,
    currentPage,
    totalPages,
    hasNextPage,
    hasPrevPage,
    loading: assetsLoading,
    isRefreshing,
    error: assetsError,
  } = useClientAssets(
    clientId!,
    page,
    PAGE_SIZE,
    debouncedSearch,
    statusFilter !== "all" ? statusFilter : undefined,
    typeFilter !== "all" ? typeFilter : undefined,
    propertyFilter !== "all" ? propertyFilter : undefined,
  );

  // Get client/project data
  const client = useMemo(() => {
    return projects.find((p) => p.id === clientId);
  }, [projects, clientId]);

  useDocumentTitle(client ? buildPageTitle([client.name, "Assets"]) : null);

  const properties = client?.properties || [];
  const propertyIds = useMemo(() => properties.map((p) => p.id), [properties]);

  // Fetch filter options independently
  const filterOptions = useFilterOptions(clientId ?? undefined, propertyIds);

  // Handle page changes
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // MVP2-282: Reset to page 1 only when search term actually changes (not on every debounce)
  useEffect(() => {
    if (prevSearchRef.current !== debouncedSearch) {
      setPage(1);
      prevSearchRef.current = debouncedSearch;
    }
  }, [debouncedSearch]);

  // MVP2-282: Separate effect for filter changes
  useEffect(() => {
    setPage(1);
  }, [statusFilter, typeFilter, propertyFilter]);

  // Clear all filters
  const clearAllFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setTypeFilter("all");
    setPropertyFilter("all");
  };

  const hasActiveFilters = Boolean(
    searchTerm ||
      statusFilter !== "all" ||
      typeFilter !== "all" ||
      propertyFilter !== "all",
  );

  const [exportingFormat, setExportingFormat] = useState<AssetExportFormat | null>(null);
  const { enabled: exportsEnabled } = useExportsEnabled(clientId);
  const handleExport = async (format: AssetExportFormat) => {
    if (!clientId) return;
    setExportingFormat(format);
    try {
      // typeFilter holds a category_id; exportAssets filters by category name.
      const categoryName =
        typeFilter !== "all"
          ? filterOptions.categories.find((c) => c.id === typeFilter)?.name
          : undefined;
      await exportAssets({
        projectId: clientId,
        propertyId: propertyFilter !== "all" ? propertyFilter : undefined,
        category: categoryName,
        label: client?.name || "assets",
        format,
      });
      toast({ title: `Exported as ${format.toUpperCase()}` });
    } catch (err) {
      toast({
        title: "Export failed",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    } finally {
      setExportingFormat(null);
    }
  };

  const loading = projectsLoading || summaryLoading;

  if (loading) {
    return <ClientAssetsLoadingSkeleton />;
  }

  if (!client) {
    return (
      <div className="flex items-center justify-center h-full min-h-screen">
        <div className="text-center space-y-2">
          <Package className="h-12 w-12 text-muted-foreground mx-auto" />
          <h2 className="text-2xl font-semibold">Client not found</h2>
          <p className="text-muted-foreground">The requested client could not be found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-accent/20 flex flex-col overflow-hidden">
      <ClientAssetsHeader
        clientName={client.name}
        logoUrl={client.logo_url}
        totalAssets={summary.totalAssets}
        propertiesCount={summary.propertiesCount}
        exportsEnabled={exportsEnabled}
        exportingFormat={exportingFormat}
        onExport={handleExport}
      />

      <div className="flex-1 overflow-auto">
        <div className="p-6 space-y-6">
          <ClientAssetsFilters
            searchTerm={searchTerm}
            debouncedSearch={debouncedSearch}
            statusFilter={statusFilter}
            typeFilter={typeFilter}
            propertyFilter={propertyFilter}
            properties={properties}
            filterOptions={filterOptions}
            assetsLoading={assetsLoading}
            hasActiveFilters={hasActiveFilters}
            onSearchChange={setSearchTerm}
            onStatusChange={setStatusFilter}
            onTypeChange={setTypeFilter}
            onPropertyChange={setPropertyFilter}
            onClearAll={clearAllFilters}
          />

          {/* Error State */}
          {assetsError && (
            <Card className="border-destructive/50 bg-destructive/5">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 text-destructive">
                  <AlertCircle className="h-5 w-5" />
                  <div>
                    <p className="font-semibold">Error Loading Assets</p>
                    <p className="text-sm">{assetsError}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* MVP2-282: Asset Table with loading overlay to prevent flicker */}
          <div className="relative">
            {isRefreshing && (
              <div className="absolute inset-0 bg-background/50 backdrop-blur-[1px] z-10 pointer-events-none rounded-lg" />
            )}
            <ClientAssetsTable
              assets={assets}
              loading={assetsLoading && !isRefreshing}
              onAssetClick={(assetId) => setSelectedAssetId(assetId)}
              selectedAssetId={selectedAssetId ?? undefined}
            />
          </div>

          {/* Pagination Controls */}
          {!assetsLoading && assets.length > 0 && (
            <ClientAssetsPagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={PAGE_SIZE}
              hasNextPage={hasNextPage}
              hasPrevPage={hasPrevPage}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </div>

      <AssetDetailPanelLoader
        assetId={selectedAssetId}
        onClose={() => setSelectedAssetId(null)}
      />
    </div>
  );
}
